import { useEffect } from 'react';
import {
  type Control,
  type UseFormSetValue,
  useFormState,
  type UseFormTrigger,
} from 'react-hook-form';

import type { Order } from '~hooks/useOrders/models';
import type { WayPoint } from '~hooks/useSites/models';

import type {
  OrderFormV3FieldValues,
  OrderWaypointSiteValue,
} from '../OrderFormV3Context';

/**
 * Triggers: the watched `pickUpWayPoint.site` / `dropOffWayPoint.site`
 *           (via the Provider) and the form's `isSubmitted` flag.
 * Writes:   the Provider's `pickupChangedFromSaved` flag (sticky — feeds the
 *           shouldOverrideRatesWithSrs derivation); `pickUpWayPoint.id` /
 *           `dropOffWayPoint.id` form values (saved-waypoint id restore on
 *           persisted orders); waypoint re-validation via trigger(); the
 *           Provider's showPickUpContact/showDropOffContact (mount auto-open).
 * Guards:   the divergence flag flips once and stays (legacy's sticky
 *           A→B→A semantics); the id reset runs only for persisted orders
 *           (`defaultOrder.orderId`); re-validation only after a submit
 *           attempt (mimics 'onChange' revalidation); the auto-open runs
 *           once at mount, only for default waypoints that carry a contact
 *           name.
 * Legacy:   the legacy form's pickup-divergence tracking, revalidation,
 *           waypoint id reset, and contact auto-open.
 * Test:     __tests__/OrderFormV3Context.test.tsx
 *           ("waypoint validation/identity sync").
 */
export function useWaypointValidationSync({
  control,
  defaultOrder,
  defaultPickUpWaypoint,
  defaultDropOffWaypoint,
  watchedPickUpWaypointSite,
  watchedDropOffWaypointSite,
  trigger,
  setValue,
  pickupChangedFromSaved,
  setPickupChangedFromSaved,
  setShowPickUpContact,
  setShowDropOffContact,
}: {
  control: Control<OrderFormV3FieldValues>;
  defaultOrder: Order | null;
  defaultPickUpWaypoint: WayPoint | undefined;
  defaultDropOffWaypoint: WayPoint | undefined;
  watchedPickUpWaypointSite: OrderWaypointSiteValue;
  watchedDropOffWaypointSite: OrderWaypointSiteValue;
  trigger: UseFormTrigger<OrderFormV3FieldValues>;
  setValue: UseFormSetValue<OrderFormV3FieldValues>;
  pickupChangedFromSaved: boolean;
  setPickupChangedFromSaved: (changed: boolean) => void;
  setShowPickUpContact: (show: boolean) => void;
  setShowDropOffContact: (show: boolean) => void;
}): void {
  // Legacy destructures isSubmitted off its own useForm; the module takes
  // the equivalent RHF subscription itself, like useFormStateChangeNotifier.
  const { isSubmitted } = useFormState({ control });

  // Sticky flag: once the dispatcher touches the pickup site in this
  // session, SRS becomes the source of truth for the freight rates, even if
  // they then revert to the order's saved pickup. Without the sticky
  // behavior a momentary A→B→A round-trip would snap back to the (possibly
  // hand-edited) stamped rates instead of re-applying the SRS.
  useEffect(() => {
    if (pickupChangedFromSaved) return;
    const savedSiteId = defaultOrder?.pickUpWayPoints?.[0]?.site?.id;
    const watchedSiteId = watchedPickUpWaypointSite?.id;
    if (savedSiteId && watchedSiteId && savedSiteId !== watchedSiteId) {
      setPickupChangedFromSaved(true);
    }
    // Legacy deps verbatim.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pickupChangedFromSaved,
    watchedPickUpWaypointSite?.id,
    defaultOrder?.pickUpWayPoints,
  ]);

  // Necessary for mimicking 'onChange' revalidation.
  useEffect(() => {
    if (isSubmitted) {
      void trigger('pickUpWayPoint');
      void trigger('dropOffWayPoint');
    }
    // Legacy deps verbatim.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedPickUpWaypointSite?.id, watchedDropOffWaypointSite?.id, isSubmitted]);

  // On persisted orders, a site swap must keep the SAVED waypoint id in the
  // form value so the update payload edits the existing waypoint instead of
  // creating a duplicate. (Legacy carries over its own
  // "TODO: see comment in JobEditForm.tsx for why this is broken" — the
  // behavior is ported as-is, quirks included.)
  useEffect(() => {
    if (defaultOrder?.orderId) {
      if (
        defaultOrder?.pickUpWayPoints?.[0]?.site?.id !== watchedPickUpWaypointSite?.id
      ) {
        setValue('pickUpWayPoint.id', `${defaultOrder?.pickUpWayPoints?.[0]?.id}`, {
          shouldDirty: false,
        });
      }
      if (
        defaultOrder?.dropOffWayPoints?.[0]?.site?.id !== watchedDropOffWaypointSite?.id
      ) {
        setValue('dropOffWayPoint.id', `${defaultOrder?.dropOffWayPoints?.[0]?.id}`, {
          shouldDirty: false,
        });
      }
    }
    // Legacy deps verbatim — the watched site OBJECTS.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedPickUpWaypointSite, watchedDropOffWaypointSite]);

  // Open the contact sections at mount when the default waypoints already
  // carry a contact name (saved orders re-open with their contacts
  // visible).
  useEffect(() => {
    if (defaultPickUpWaypoint?.contact?.name) {
      setShowPickUpContact(true);
    }
    if (defaultDropOffWaypoint?.contact?.name) {
      setShowDropOffContact(true);
    }
    // Mount-only, like legacy (deps []).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
